/**
 * Reading a GeoPackage in the browser: sql.js for the tables, a small WKB decoder for the
 * geometry (GeoPackage binary is an 8-byte header, an optional envelope, then plain WKB).
 */
import type { Geometry, Position } from 'geojson';
import initSqlJs, { type Database, type SqlJsStatic } from 'sql.js';
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';

export type GpkgErrorCode = 'not_gpkg' | 'no_feature_table' | 'unsupported_geometry';

export class GpkgError extends Error {
  constructor(
    public readonly code: GpkgErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'GpkgError';
  }
}

const SQLITE_MAGIC = 'SQLite format 3\0';

let sqlJs: Promise<SqlJsStatic> | null = null;
function engine(): Promise<SqlJsStatic> {
  sqlJs ??= initSqlJs({ locateFile: () => wasmUrl });
  return sqlJs;
}

export function isSqlite(bytes: Uint8Array): boolean {
  if (bytes.length < SQLITE_MAGIC.length) return false;
  return new TextDecoder().decode(bytes.subarray(0, SQLITE_MAGIC.length)) === SQLITE_MAGIC;
}

export async function openGpkg(bytes: Uint8Array): Promise<Database> {
  if (!isSqlite(bytes)) throw new GpkgError('not_gpkg');
  const SQL = await engine();
  return new SQL.Database(bytes);
}

export interface FeatureTable {
  table: string;
  geometryColumn: string;
  srsId: number;
  /** `EPSG:<code>` of the table's spatial reference system, or null when it is undefined or not EPSG. */
  crs: string | null;
}

function crsOf(srsId: number, organization: unknown, coordsysId: unknown): string | null {
  // GeoPackage reserves 0 for undefined geographic (taken as WGS84) and -1 for undefined cartesian.
  if (srsId === 4326 || srsId === 0) return 'EPSG:4326';
  if (srsId === -1) return null;
  if (typeof organization === 'string' && organization.toUpperCase() === 'EPSG' && typeof coordsysId === 'number') {
    return `EPSG:${coordsysId}`;
  }
  return null;
}

/** Feature tables in `gpkg_contents` order, with the geometry column and the CRS each one declares. */
export function featureTables(db: Database): FeatureTable[] {
  let result;
  try {
    result = db.exec(
      `SELECT c.table_name, g.column_name, c.srs_id, s.organization, s.organization_coordsys_id FROM gpkg_contents c
       JOIN gpkg_geometry_columns g ON g.table_name = c.table_name
       LEFT JOIN gpkg_spatial_ref_sys s ON s.srs_id = c.srs_id
       WHERE c.data_type = 'features' ORDER BY c.rowid`,
    )[0];
  } catch (error) {
    if (error instanceof Error && /no such table/i.test(error.message)) throw new GpkgError('not_gpkg', 'no gpkg_contents');
    throw error;
  }
  return (result?.values ?? []).map(([table, column, srs, organization, coordsysId]) => ({
    table: String(table),
    geometryColumn: String(column),
    srsId: Number(srs),
    crs: crsOf(Number(srs), organization, coordsysId),
  }));
}

const ENVELOPE_BYTES = [0, 32, 48, 48, 64];

/** GeoPackage binary → GeoJSON geometry. Z and M are dropped; an empty blob is null. */
export function decodeGeometry(blob: Uint8Array): Geometry | null {
  if (blob.length === 0) return null;
  if (blob.length < 8 || blob[0] !== 0x47 || blob[1] !== 0x50) throw new GpkgError('unsupported_geometry', 'not a GeoPackage geometry');
  const flags = blob[3]!;
  if ((flags >> 4) & 1) return null; // empty geometry flag
  const envelope = ENVELOPE_BYTES[(flags >> 1) & 0b111];
  if (envelope === undefined) throw new GpkgError('unsupported_geometry', 'bad envelope indicator');
  if (blob.length < 8 + envelope) throw new GpkgError('unsupported_geometry', 'truncated geometry');
  try {
    return readWkb(new DataView(blob.buffer, blob.byteOffset + 8 + envelope, blob.byteLength - 8 - envelope));
  } catch (error) {
    if (error instanceof RangeError) throw new GpkgError('unsupported_geometry', 'truncated geometry');
    throw error;
  }
}

function readWkb(view: DataView): Geometry {
  let offset = 0;
  const geometry = (): Geometry => {
    const little = view.getUint8(offset) === 1;
    offset += 1;
    const raw = view.getUint32(offset, little);
    offset += 4;
    // EWKB flag bits and the ISO 1000/2000/3000 offsets both say "extra dimensions".
    const iso = raw & 0x0fffffff;
    const isoDims = Math.floor((iso % 10000) / 1000);
    const hasZ = (raw & 0x80000000) !== 0 || isoDims === 1 || isoDims === 3;
    const hasM = (raw & 0x40000000) !== 0 || isoDims === 2 || isoDims === 3;
    const type = iso % 1000;
    const dims = 2 + (hasZ ? 1 : 0) + (hasM ? 1 : 0);
    const position = (): Position => {
      const x = view.getFloat64(offset, little);
      const y = view.getFloat64(offset + 8, little);
      offset += 8 * dims;
      return [x, y];
    };
    const count = (minBytes: number): number => {
      const n = view.getUint32(offset, little);
      offset += 4;
      if (n * minBytes > view.byteLength - offset) throw new GpkgError('unsupported_geometry', 'bad count');
      return n;
    };
    const ring = (): Position[] => {
      const n = count(8 * dims);
      return Array.from({ length: n }, position);
    };
    const many = <T>(read: () => T): T[] => {
      const n = count(4);
      return Array.from({ length: n }, read);
    };
    switch (type) {
      case 1:
        return { type: 'Point', coordinates: position() };
      case 2:
        return { type: 'LineString', coordinates: ring() };
      case 3:
        return { type: 'Polygon', coordinates: many(ring) };
      case 4:
        return { type: 'MultiPoint', coordinates: many(() => (geometry() as GeoJSON.Point).coordinates) };
      case 5:
        return { type: 'MultiLineString', coordinates: many(() => (geometry() as GeoJSON.LineString).coordinates) };
      case 6:
        return { type: 'MultiPolygon', coordinates: many(() => (geometry() as GeoJSON.Polygon).coordinates) };
      default:
        throw new GpkgError('unsupported_geometry', `wkb type ${raw}`);
    }
  };
  return geometry();
}

/**
 * GeoJSON geometry → GeoPackage binary (little-endian, no envelope, 2D) tagged with `srsId`.
 * The inverse of `decodeGeometry`, used to write a reprojected table back.
 */
export function encodeGeometry(geometry: Geometry, srsId: number): Uint8Array {
  const parts: number[] = [];
  const u8 = (v: number) => parts.push(v & 0xff);
  const u32 = (v: number) => parts.push(v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff);
  const f64 = new DataView(new ArrayBuffer(8));
  const pos = (p: Position) => {
    f64.setFloat64(0, p[0]!, true);
    for (let i = 0; i < 8; i++) parts.push(f64.getUint8(i));
    f64.setFloat64(0, p[1]!, true);
    for (let i = 0; i < 8; i++) parts.push(f64.getUint8(i));
  };
  const ring = (r: Position[]) => {
    u32(r.length);
    r.forEach(pos);
  };
  const write = (g: Geometry): void => {
    u8(1);
    switch (g.type) {
      case 'Point':
        u32(1);
        pos(g.coordinates);
        return;
      case 'LineString':
        u32(2);
        ring(g.coordinates);
        return;
      case 'Polygon':
        u32(3);
        u32(g.coordinates.length);
        g.coordinates.forEach(ring);
        return;
      case 'MultiPoint':
        u32(4);
        u32(g.coordinates.length);
        g.coordinates.forEach((c) => write({ type: 'Point', coordinates: c }));
        return;
      case 'MultiLineString':
        u32(5);
        u32(g.coordinates.length);
        g.coordinates.forEach((c) => write({ type: 'LineString', coordinates: c }));
        return;
      case 'MultiPolygon':
        u32(6);
        u32(g.coordinates.length);
        g.coordinates.forEach((c) => write({ type: 'Polygon', coordinates: c }));
        return;
      case 'GeometryCollection':
        // Never produced by decodeGeometry either.
        throw new GpkgError('unsupported_geometry', 'GeometryCollection');
    }
  };
  // Header: magic 'GP', version 0, flags (bit 0: little-endian; envelope 0), srs_id.
  u8(0x47);
  u8(0x50);
  u8(0);
  u8(1);
  u32(srsId);
  write(geometry);
  return Uint8Array.from(parts);
}
